import { useEffect, useMemo } from 'react';

import { AwardingBodyIdentifiers, B2B_SUB_SYSTEM_SHORT_CODE, B2C_SUB_SYSTEM_SHORT_CODE, ExamType } from 'core';
import { BasketItem, useLazyRefreshPricesQuery } from 'store/basket/basketAPI';
import {
  CandidateProductDetails,
  DEFAULT_IDENTITY_DOCUMENT_VALUES,
  GroupAwards,
  GroupAwardsItem,
  IdentityDocument,
  RegistrationCandidateDetails,
} from 'store/candidate/types';
import { selectBasketExams } from 'store/basket/basketSlice';
import { selectCandidate, selectCandidateProductDetails } from 'store/candidate/candidateSlice';
import { useAppSelector } from 'store';
import { useGetGroupAwardsDataQuery } from 'store/candidate/candidateAPI';
import { selectPreparationCentre } from 'store/country/countrySlice';
import { useEligibilityCheck } from "pages/candidate-product-details/useEligibilityCheck";

export const useCandidateProductDetailsInitialValues = (countryId: string) => {
  const storedProductDetails = useAppSelector(selectCandidateProductDetails);
  const basketItems = useAppSelector(selectBasketExams);
  const candidateDetails = useAppSelector(selectCandidate);
  const pcInfo = useAppSelector(selectPreparationCentre);
    const {isLoading : eligibilityCheckIsLoading,...eligibilityCheck } = useEligibilityCheck()
  const { awardingBodyId, sessionId, districtId, sessionStageId } = useMemo(
    () => ({
      awardingBodyId: basketItems[0]?.awardingBodyId,
      sessionId: basketItems[0]?.sessionId,
      districtId: basketItems[0]?.districtId,
      sessionStageId: basketItems[0]?.sessionStageId,
    }),
    [basketItems]
  );

  const [refreshPrices, { data: refreshedPrices, isFetching: refreshedPricesLoading }] = useLazyRefreshPricesQuery();
  const { data: groupAwardItems, isFetching: groupAwardsLoading } = useGetGroupAwardsDataQuery({
    candidateId: candidateDetails?.id,
    subSystem: pcInfo ? B2B_SUB_SYSTEM_SHORT_CODE : B2C_SUB_SYSTEM_SHORT_CODE,
    countryId: Number(countryId),
    districtId: districtId ?? 0,
    sessionId: sessionId ?? 0,
    sessionStageId: sessionStageId ?? 0,
  });

  const exams = useMemo(
    () =>
      !!refreshedPrices
        ? basketItems.map((item) => {
            const newFee = refreshedPrices.find((x) => x.districtExamId === item.districtExamId)?.fee;
            return { ...item, fee: newFee ?? item.fee };
          })
        : basketItems,
    [basketItems, refreshedPrices]
  );

  const initialValues = useMemo<CandidateProductDetails>(
    () => ({
      ...(storedProductDetails
        ? storedProductDetails
        : {
            ...getIdentityDocumentValues(candidateDetails),
            ...getUciInitialData(awardingBodyId, candidateDetails),
          }),
      examRetake: getExamRetakeInitialData(exams),
      groupAwards: getGroupAwardsInitialData(groupAwardItems, storedProductDetails?.groupAwards),
      previousCentreNumber: storedProductDetails?.previousCentreNumber ?? '',
      previousCandidateNumber: storedProductDetails?.previousCandidateNumber ?? '',
            proceedWithCashinRequired: eligibilityCheck.proceedWithCashinRequired,
            proceedWithCashin: false
    }),
    [storedProductDetails, awardingBodyId, candidateDetails, exams, groupAwardItems]
  );

  useEffect(() => {
    if (awardingBodyId === AwardingBodyIdentifiers.AQA) {
      refreshPrices({ basketItems, candidateId: candidateDetails?.id, sessionId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awardingBodyId]);

  return {
    initialValues,
    exams,
        isLoading: refreshedPricesLoading || groupAwardsLoading || eligibilityCheckIsLoading,
    awardingBodyId,
    groupAwardItems,
        eligibilityCheck: eligibilityCheck,
  };
};

function getIdentityDocumentValues(candidateDetails: RegistrationCandidateDetails | undefined): IdentityDocument {
  if (!!candidateDetails && candidateDetails.selectedIdentificationDocument > 0) {
    return {
      selectedIdentificationDocument:
        candidateDetails.selectedIdentificationDocument.toString() ??
        DEFAULT_IDENTITY_DOCUMENT_VALUES.selectedIdentificationDocument,
      identificationDocNo: candidateDetails.identificationDocNo ?? DEFAULT_IDENTITY_DOCUMENT_VALUES.identificationDocNo,
      identificationDocumentHasNoExpiryDate:
        candidateDetails.identificationDocumentHasNoExpiryDate ??
        DEFAULT_IDENTITY_DOCUMENT_VALUES.identificationDocumentHasNoExpiryDate,
      identificationDocExpiryDate:
        candidateDetails.identificationDocExpiryDate ?? DEFAULT_IDENTITY_DOCUMENT_VALUES.identificationDocExpiryDate,
      confirmationSelected:
        candidateDetails.confirmationSelected ?? DEFAULT_IDENTITY_DOCUMENT_VALUES.confirmationSelected,
    };
  }

  return DEFAULT_IDENTITY_DOCUMENT_VALUES;
}

function getUciInitialData(
  awardingBodyId: number | undefined,
  candidateDetails: RegistrationCandidateDetails | undefined
) {
  return awardingBodyId === AwardingBodyIdentifiers.Edexcel && !!candidateDetails?.uciEdexcel
    ? { forceSetUciNumber: true, uci: candidateDetails.uciEdexcel }
    : awardingBodyId === AwardingBodyIdentifiers.AQA && !!candidateDetails?.uciAqa
    ? { forceSetUciNumber: true, uci: candidateDetails.uciAqa }
    : { forceSetUciNumber: false, uci: '' };
}

function getExamRetakeInitialData(exams: BasketItem[]) {
  return exams.map((x) => ({
    type:
      x.retake?.type ??
      (x.retakeSelected
        ? x.isCarryForward
          ? ExamType.RetakeCarryForward
          : ExamType.Retake
        : x.isCarryForward
        ? ExamType.CarryForward
        : ExamType.Standard),
    previousCandidateNumber: x.retake?.previousCandidateNumber || '',
    previousCenterNumber: x.retake?.previousCenterNumber || '',
  }));
}

function getGroupAwardsInitialData(groupAwards: GroupAwardsItem[] | undefined, storedModel: GroupAwards | undefined) {
  return {
    agreementConfirmed: storedModel?.agreementConfirmed ?? false,
    groupAwardExams:
      groupAwards?.map((x) => {
        const storedItem = storedModel?.groupAwardExams.find((y) => y.examOptionId === x.examOptionId);
        return {
          examOptionId: x.examOptionId,
          sessionStageId: x.sessionStageId,
          isSelected: storedItem?.isSelected || x.isSelected,
          previousCenterNumber: storedItem?.previousCenterNumber ?? x.previousCenterNumber ?? '',
          previousCandidateNumber: storedItem?.previousCandidateNumber ?? x.previousCandidateNumber ?? '',
        };
      }) ?? [],
  };
}
